import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import {
  CAvatar,
  CDropdown,
  CDropdownDivider,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilAccountLogout, cilUser } from "@coreui/icons";
import { routePaths } from "../../routes/route-paths";
import { useCustomerProfile } from "../../api/hooks/useCustomerProfile";

const avatarPlaceholder =
  "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSIyMCIgZmlsbD0iIzZjNzU3ZCIvPjxwYXRoIGQ9Ik0yMCAyMGM0LjE0MiAwIDcuNS0zLjM1OCA3LjUtNy41UzI0LjE0MiA1IDIwIDVzLTcuNSAzLjM1OC03LjUgNy41IDMuMzU4IDcuNSA3LjUgNy41em0wIDMuNzVjLTUuMDA0IDAtMTUgMi41MDQtMTUgNy41VjM1aDMwdi0zLjc1YzAtNC45OTYtOS45OTYtNy41LTE1LTcuNXoiIGZpbGw9IiNmZmYiLz48L3N2Zz4=";

function fallbackFirstNameFromAccount(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed || trimmed.includes("@")) return null;
  return trimmed.split(/\s+/)[0] ?? null;
}

function fallbackLastNameFromAccount(name: string | null | undefined): string | null {
  if (!name) return null;
  const trimmed = name.trim();
  if (!trimmed || trimmed.includes("@")) return null;
  const parts = trimmed.split(/\s+/);
  return parts.length > 1 ? parts.slice(1).join(" ") : null;
}

// Array.from takes the first character, not the first UTF-16 unit, so an accented or emoji name
// gives a whole letter.
function initialsOf(firstName: string | null, lastName: string | null): string | null {
  const first = firstName ? Array.from(firstName)[0] : undefined;
  if (!first) return null;
  const last = lastName ? (Array.from(lastName)[0] ?? "") : "";
  return `${first}${last}`.toUpperCase();
}

export function AppHeaderDropdown() {
  const { t } = useTranslation();
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const profile = useCustomerProfile();
  const account = accounts[0];

  const firstName =
    profile.data?.firstName?.trim() ||
    fallbackFirstNameFromAccount(account?.name) ||
    null;
  const lastName =
    profile.data?.lastName?.trim() ||
    fallbackLastNameFromAccount(account?.name) ||
    null;
  const email =
    profile.data?.email?.trim() || account?.username?.trim() || null;
  const initials = initialsOf(firstName, lastName);

  // Given the account, MSAL sends its login_hint claim as the logout hint, so Entra signs it out
  // without first asking which account is leaving.
  const handleLogout = () => {
    const logoutRequest = { account, postLogoutRedirectUri: window.location.origin };
    return instance.logoutRedirect(logoutRequest);
  };

  return (
    <CDropdown variant="nav-item" placement="bottom-end">
      <CDropdownToggle className="py-0 pe-0" caret={false}>
        {initials ? (
          <CAvatar color="primary" textColor="white" size="md">
            {initials}
          </CAvatar>
        ) : (
          <CAvatar src={avatarPlaceholder} size="md" />
        )}
      </CDropdownToggle>
      <CDropdownMenu className="pt-0" style={{ minWidth: 240 }}>
        <CDropdownHeader className="bg-body-secondary fw-semibold mb-2">
          {t("common:header.account")}
        </CDropdownHeader>

        {firstName && (
          <CDropdownItem disabled className="text-body small d-flex justify-content-between gap-3">
            <span className="text-body-secondary">First name</span>
            <span className="text-body fw-semibold">{firstName}</span>
          </CDropdownItem>
        )}
        {lastName && (
          <CDropdownItem disabled className="text-body small d-flex justify-content-between gap-3">
            <span className="text-body-secondary">Last name</span>
            <span className="text-body fw-semibold">{lastName}</span>
          </CDropdownItem>
        )}
        {email && (
          <CDropdownItem disabled className="text-body small d-flex justify-content-between gap-3">
            <span className="text-body-secondary">Email</span>
            <span
              className="text-body fw-semibold text-truncate"
              style={{ maxWidth: 160 }}
              title={email}
            >
              {email}
            </span>
          </CDropdownItem>
        )}

        <CDropdownDivider />
        <CDropdownItem
          as="button"
          type="button"
          onClick={() => navigate(routePaths.profile)}
        >
          <CIcon icon={cilUser} className="me-2" />
          {t("common:navigation.profile")}
        </CDropdownItem>
        <CDropdownItem as="button" type="button" onClick={handleLogout}>
          <CIcon icon={cilAccountLogout} className="me-2" />
          {t("common:header.logout")}
        </CDropdownItem>
      </CDropdownMenu>
    </CDropdown>
  );
}
